<template>
  <div>
    <div ref="amalgamationModal" class="modal fade amalgamation-history-modal" tabindex="-1" role="dialog" aria-labelledby="amalgamationHistoryModalLabel"
         aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-uppercase" id="amalgamationHistoryModalLabel">Amalgamation History</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading amalgamation history...</p>
            </div>

            <div v-else>
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Amalgamation history for property <strong>{{ property.LRNumber }}</strong>.</p>
                </div>
              </div>

              <!-- Debug info -->
              <div v-if="debug" class="alert alert-warning mb-3">
                <p class="mb-1"><strong>Debug Info:</strong></p>
                <ul class="mb-0">
                  <li>Property ID: {{ propertyId || property.id }}</li>
                  <li>LR Number: {{ property.LRNumber }}</li>
                  <li>Original Properties: {{ originalProperties.length }}</li>
                  <li>New Properties: {{ newProperties.length }}</li>
                </ul>
              </div>

              <!-- Show original properties -->
              <div v-if="originalProperties.length > 0" class="mb-4">
                <h6 class="fw-bold">New Amalgamated Property</h6>
                <p>This property was created through amalgamation of the following properties:</p>
                <div class="table-responsive">
                  <table class="table table-bordered table-striped">
                    <thead>
                    <tr>
                      <th>Original LR Number</th>
                      <th>Percel Size</th>
                      <th>Amalgamation Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(record, index) in originalProperties" :key="'orig-'+index">
                      <td>{{ record.original_lr_number }}</td>
                      <td>{{ record.percel_size }} Ha</td>
                      <td>{{ formatDate(record.amalgamation_date) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Show new properties -->
              <div v-if="newProperties.length > 0" class="mb-4">
                <h6 class="fw-bold">Part of Amalgamation</h6>
                <p>This property was amalgamated into the following new properties:</p>
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead>
                    <tr>
                      <th>New LR Number</th>
                      <th>Amalgamation Reference</th>
                      <th>Amalgamation Date</th>
                      <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(newProp, index) in newProperties" :key="'new-'+index">
                      <td>{{ newProp.new_lr_number }}</td>
                      <td>{{ newProp.amalgamation_reference }}</td>
                      <td>{{ formatDate(newProp.amalgamation_date) }}</td>
                      <td>
                        <button @click="viewProperty(newProp.new_property_id)" class="btn btn-sm btn-primary">
                          <i class="mdi mdi-eye"></i> View
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- No history message -->
              <div v-if="!loading && originalProperties.length === 0 && newProperties.length === 0" class="text-center py-3">
                <p>No amalgamation history found for this property.</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { landRate, execute } from '@/api';
import moment from 'moment';

export default {
  name: 'AmalgamationHistory',
  props: {
    property: {
      type: Object,
      required: true
    },
    propertyId: {
      type: [Number, String],
      required: false,
      default: null
    },
    debug: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      originalProperties: [],
      newProperties: [],
      modalInstance: null
    };
  },

  mounted() {
    // We'll use this to ensure the modal is initialized correctly
    this.initializeModal();
  },

  beforeUnmount() {
    // Clean up the modal when component is unmounted
    if (this.modalInstance) {
      this.modalInstance.dispose();
    }
  },

  methods: {
    initializeModal() {
      // Use Vue ref to get modal element properly
      const modalEl = this.$refs.amalgamationModal;
      if (modalEl && typeof bootstrap !== 'undefined') {
        this.modalInstance = new bootstrap.Modal(modalEl);
      }
    },

    async openModal() {
      // Reset data first
      this.loading = true;
      this.originalProperties = [];
      this.newProperties = [];

      // Make sure modal is initialized
      if (!this.modalInstance) {
        this.initializeModal();
      }

      // Show modal
      if (this.modalInstance) {
        this.modalInstance.show();

        // Load data after modal is shown
        setTimeout(() => {
          this.loadAmalgamationHistory();
        }, 100);
      } else {
        console.error('Modal instance not available');
        // Try direct DOM approach as fallback
        try {
          const modalEl = document.querySelector('.amalgamation-history-modal');
          if (modalEl) {
            const bsModal = new bootstrap.Modal(modalEl);
            bsModal.show();

            setTimeout(() => {
              this.loadAmalgamationHistory();
            }, 100);
          }
        } catch (error) {
          console.error('Error showing modal:', error);
          // Just load the data anyway
          this.loadAmalgamationHistory();
        }
      }
    },

    async loadAmalgamationHistory() {
      try {
        const data = new FormData();
        data.append('function', 'getAmalgamationHistory');
        data.append('propertyId', this.propertyId || this.property.id);
        data.append('LRNumber', this.property.LRNumber);

        const response = await execute(data, landRate);

        if (response.data.success) {
          const historyData = response.data.data.amalgamationHistory;

          // Handle empty data
          if (!historyData || historyData.length === 0) {
            console.log('No amalgamation history data found');
            this.loading = false;
            return;
          }

          // Normalize current property identifiers
          const currentPropertyId = (this.propertyId || this.property.id).toString();
          const currentLRNumber = this.property.LRNumber.replace(/\\\//g, '/');

          // Process each history item
          const origProps = [];
          const newProps = [];

          historyData.forEach(item => {
            // Make safety copies of all data to prevent undefined errors
            const safeItem = { ...item };

            // Normalize string values and handle potential undefined values
            const newId = (safeItem.new_property_id || '').toString();
            const newLR = (safeItem.new_lr_number || '').replace(/\\\//g, '/');
            const origId = (safeItem.original_property_id || '').toString();
            const origLR = (safeItem.original_lr_number || '').replace(/\\\//g, '/');

            // Split compound LR numbers and normalize
            const newLRParts = newLR.split('|').map(part => part.trim()).filter(Boolean);

            // Check if current property is the new amalgamated property
            if (newId === currentPropertyId || newLRParts.includes(currentLRNumber)) {
              origProps.push(safeItem);
            }

            // Check if current property was part of amalgamation
            if (origId === currentPropertyId || origLR === currentLRNumber) {
              newProps.push(safeItem);
            }
          });

          // Update reactive properties
          this.originalProperties = [...origProps];
          this.newProperties = [...newProps];

          console.log('Amalgamation history loaded:', {
            propertiesAsOriginal: this.originalProperties.length,
            propertiesAsNew: this.newProperties.length,
            originalProperties: this.originalProperties,
            newProperties: this.newProperties
          });
        }
      } catch (error) {
        console.error('Error fetching amalgamation history:', error);
      } finally {
        this.loading = false;
      }
    },

    viewProperty(propertyId) {
      this.$emit('viewProperty', propertyId);

      // Close modal properly
      if (this.modalInstance) {
        this.modalInstance.hide();
      } else {
        // Fallback method
        try {
          const modalEl = document.querySelector('.amalgamation-history-modal');
          const bsModal = bootstrap.Modal.getInstance(modalEl);
          if (bsModal) {
            bsModal.hide();
          }
        } catch (error) {
          console.error('Error hiding modal:', error);
        }
      }
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    }
  }
};
</script>